import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Me } from "../../api/users";
import { PostGet } from "../../api/post";
import { GetAcativeRestaurants } from "../../api/restaurant";
import { listServiceNotes } from "../../api/service_note";
import { TYLevel } from "../../constants";

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMonthStartDate() {
  const date = new Date();
  date.setDate(1);
  return formatDate(date);
}

function getTodayDate() {
  return formatDate(new Date());
}

const STATUS_OPTIONS = [
  { value: 0, label: "Все статусы" },
  { value: 1, label: "Не согласовано" },
  { value: 2, label: "На доработке" },
  { value: 3, label: "Согласовано" },
  { value: 4, label: "Отклонено" },
];

function statusTone(statusId, color) {
  if (color === "green" || Number(statusId) === 3) return "ok";
  if (color === "yellow" || Number(statusId) === 2) return "rework";
  if (Number(statusId) === 4) return "reject";
  return "wait";
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function formatWeek(startDate, endDate) {
  if (!startDate || !endDate) return "—";
  return `${formatDay(startDate)} — ${formatDay(endDate)}`;
}

const ServiceNotesReport = ({ token }) => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(getMonthStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [restaurantId, setRestaurantId] = useState(0);
  const [statusId, setStatusId] = useState(0);
  const [restaurantList, setRestaurantList] = useState([]);
  const [accessLevel, setAccessLevel] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);

  const canPickRestaurant = accessLevel >= TYLevel;

  async function fetchNotes({
    start = startDate,
    end = endDate,
    restaurant = restaurantId,
    status = statusId,
    pickRestaurant = canPickRestaurant,
  } = {}) {
    if (!start || !end) {
      setError("Укажите начало и конец периода");
      return;
    }
    if (start > end) {
      setError("Дата начала не может быть позже даты окончания");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const data = await listServiceNotes(token, {
        start_date: start,
        end_date: end,
        restaurant_id: pickRestaurant ? restaurant : 0,
        status_id: status,
      });
      if (data?.detail && !Array.isArray(data)) {
        setError(errorText(data, "Не удалось загрузить служебные записки"));
        setItems([]);
        return;
      }
      setItems(Array.isArray(data) ? data : []);
      setLoaded(true);
    } catch (err) {
      console.error(err);
      setError("Не удалось загрузить служебные записки");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const user = await Me(token);
        const post = await PostGet(token, user.post_id);
        if (cancelled) return;
        setAccessLevel(post.access_level);
        const pickRestaurant = post.access_level >= TYLevel;
        if (pickRestaurant) {
          const restaurants = await GetAcativeRestaurants(token);
          if (!cancelled) setRestaurantList(restaurants || []);
        }
        if (!cancelled) {
          await fetchNotes({
            start: getMonthStartDate(),
            end: getTodayDate(),
            restaurant: 0,
            status: 0,
            pickRestaurant,
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Не удалось загрузить профиль");
      }
    }
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const stats = useMemo(() => {
    let wait = 0;
    let rework = 0;
    let ok = 0;
    let reject = 0;
    let applied = 0;
    for (const item of items) {
      const tone = statusTone(item.status_id, item.status_color);
      if (tone === "ok") ok += 1;
      else if (tone === "rework") rework += 1;
      else if (tone === "reject") reject += 1;
      else wait += 1;
      if (item.applied_at) applied += 1;
    }
    return { total: items.length, wait, rework, ok, reject, applied };
  }, [items]);

  return (
    <div className="sn-page">
      <div className="reports-header">
        <div>
          <h1>Служебные записки</h1>
          <p className="reports-hint">
            Изменения расписания ЧК после согласования ТУ
            {!canPickRestaurant ? " · только ваш ресторан" : ""}
          </p>
        </div>
        <div className="sn-header-actions">
          <Link to="/reports" className="reports-back-link">
            ← К отчётам
          </Link>
        </div>
      </div>

      <form
        className={`users-filters sn-filters${canPickRestaurant ? "" : " is-no-rest"}`}
        onSubmit={(event) => {
          event.preventDefault();
          fetchNotes();
        }}
      >
        <label className="users-field">
          <span>Период с</span>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>
        <label className="users-field">
          <span>Период по</span>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
        {canPickRestaurant ? (
          <label className="users-field">
            <span>Ресторан</span>
            <select
              className="form-select"
              value={restaurantId}
              onChange={(event) => setRestaurantId(Number(event.target.value))}
            >
              <option value={0}>Все рестораны</option>
              {restaurantList.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="users-field">
          <span>Статус</span>
          <select
            className="form-select"
            value={statusId}
            onChange={(event) => setStatusId(Number(event.target.value))}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          className="btn login-submit sn-filters-submit"
          disabled={loading}
        >
          {loading ? "Загрузка…" : "Показать"}
        </button>
      </form>

      {error ? <div className="login-error">{error}</div> : null}

      {loaded ? (
        <>
          <div className="chk-summary-kpis sn-kpis">
            <div className="chk-summary-kpi">
              <span>Найдено</span>
              <strong>{stats.total}</strong>
            </div>
            <div className="chk-summary-kpi">
              <span>Ожидают</span>
              <strong>{stats.wait}</strong>
            </div>
            <div className="chk-summary-kpi">
              <span>На доработке</span>
              <strong>{stats.rework}</strong>
            </div>
            <div className="chk-summary-kpi">
              <span>Согласовано</span>
              <strong>{stats.ok}</strong>
            </div>
            <div className="chk-summary-kpi">
              <span>Применено</span>
              <strong>{stats.applied}</strong>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="users-empty">
              За выбранный период служебных записок не найдено
            </div>
          ) : (
            <section className="users-list">
              <div className="users-table-wrap">
                <table className="users-table sn-table">
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Создана</th>
                      {canPickRestaurant ? <th>Ресторан</th> : null}
                      <th>Неделя</th>
                      <th>Операция</th>
                      <th>Статус</th>
                      <th>Директор</th>
                      <th className="sn-comment-cell">Комментарий</th>
                      <th>ТУ</th>
                      <th>Применено</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const tone = statusTone(item.status_id, item.status_color);
                      return (
                        <tr
                          key={item.id}
                          onClick={() =>
                            navigate(`/report-service-notes/${item.id}`)
                          }
                        >
                          <td>
                            <Link
                              to={`/report-service-notes/${item.id}`}
                              className="sn-id"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {item.id}
                            </Link>
                          </td>
                          <td className="sn-nowrap">
                            {formatDateTime(item.created)}
                          </td>
                          {canPickRestaurant ? (
                            <td>{item.restaurant_name}</td>
                          ) : null}
                          <td className="sn-nowrap">
                            {formatWeek(item.start_date, item.end_date)}
                          </td>
                          <td>
                            {item.operation_type_label ||
                              item.operation_type ||
                              "—"}
                          </td>
                          <td>
                            <span className={`chk-approve-badge is-${tone}`}>
                              {item.status_name}
                            </span>
                          </td>
                          <td>{item.creater_name}</td>
                          <td className="sn-comment-cell">
                            <span
                              className="sn-comment"
                              title={item.comment || ""}
                            >
                              {item.comment || "—"}
                            </span>
                          </td>
                          <td>{item.TY_surname_name || "—"}</td>
                          <td>
                            {item.applied_at ? (
                              <span className="chk-approve-badge is-ok">Да</span>
                            ) : item.apply_error ? (
                              <span
                                className="chk-approve-badge is-wait"
                                title={item.apply_error}
                              >
                                Ошибка
                              </span>
                            ) : (
                              <span className="sn-muted">Нет</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      ) : loading ? (
        <div className="reports-loading">Загрузка служебных записок…</div>
      ) : null}
    </div>
  );
};

export default ServiceNotesReport;
